import { IComponent } from "../../components/component.interface";
import { ColoredLastPriceComponent } from "../../components/colored-last-price.component";
import { FormatHelpers } from "../../helpers/format-helpers";
import { StringHelpers } from "../../helpers/string-helpers";
import { IChartContainer } from "../base/chart-container.interface";
import { ChartStyleSettingsModel } from "../chart-style/chart-style-settings.model";
import { ChangeFromCloseValueResult } from "./single-stock-window-change-from-close.manager";
import { SingleStockWindowDataModel } from "./single-stock-window-data.model";

export abstract class SingleStockWindowComponent implements IComponent {
    public id = StringHelpers.newGuid();
    protected element: HTMLElement;
    protected base: IChartContainer;

    protected chartStyle: ChartStyleSettingsModel | null = null;
    protected coloredLastPrice: ColoredLastPriceComponent | null = null;
    protected changeFromCloseElement: HTMLElement | null = null;
    protected changeFromCloseFirstBarElement: HTMLElement | null = null;

    protected defaultUpColor = 'rgb(0, 255, 0)';
    protected defaultDownColor = 'rgb(255, 0, 0)';

    constructor(el: HTMLElement, base: IChartContainer) {
        this.element = el;
        this.base = base;
    }

    public getClientHeight(): number {
        return this.element.clientHeight;
    }

    protected buildChangeFromCloseText(dollar: number, percent: number): string {
        const prefix = dollar > 0 ? '+' : dollar < 0 ? '-' : '';
        const percentPrefix = dollar < 0 ? '-' : '';
        return `${prefix}${FormatHelpers.numberToString(Math.abs(dollar))} (${percentPrefix}${FormatHelpers.numberToString(Math.abs(percent))}%)`;
    }

    /** The value color used in the first-bar HTML. Differs per chart (candle colors vs theme colors). */
    protected abstract getChangeFromCloseValueColor(dollar: number): string;

    protected renderChangeFromCloseFirstBar(changesValues: ChangeFromCloseValueResult) {
        if (!changesValues || !SingleStockWindowDataModel.hasValue(changesValues.changesFromCloseDollar) || !SingleStockWindowDataModel.hasValue(changesValues.changesFromClosePercentage)) {
            // No valid value yet - keep showing the last known value instead of clearing it.
            return;
        }

        const dollar = changesValues.changesFromCloseDollar;
        const percent = changesValues.changesFromClosePercentage;

        const lastPriceBgColor = this.chartStyle.getLastPriceUpColor() || '#32cd32';
        const lastPrice = this.coloredLastPrice?.getLastPriceToString() ?? '';
        const colorHex = this.getChangeFromCloseValueColor(dollar);

        const valueStyle = colorHex ? ` style="color: ${colorHex};"` : '';
        const html = `<span class="date"></span> `
            + `<span class="small-last-price" style="color: ${lastPriceBgColor};">${lastPrice}</span> `
            + `<span class="value"${valueStyle}>${this.buildChangeFromCloseText(dollar, percent)}</span>`;

        this.changeFromCloseFirstBarElement?.setInnerHtml(html);
    }

    protected renderChangeFromCloseElement(changesValues: ChangeFromCloseValueResult) {
        if (!changesValues || !SingleStockWindowDataModel.hasValue(changesValues.changesFromCloseDollar) || !SingleStockWindowDataModel.hasValue(changesValues.changesFromClosePercentage)) {
            // No valid value yet - keep showing the last known value instead of clearing it.
            return;
        }

        const dollar = changesValues.changesFromCloseDollar;
        const percent = changesValues.changesFromClosePercentage;

        const upColor = this.chartStyle?.getChangeFromCloseUpColor() ?? '';
        const downColor = this.chartStyle?.getChangeFromCloseDownColor() ?? '';

        const color = dollar < 0 ? (downColor || this.defaultDownColor) : (upColor || this.defaultUpColor);
        const text = this.buildChangeFromCloseText(dollar, percent);

        if (this.changeFromCloseElement) {
            this.changeFromCloseElement.style.color = color;
            this.changeFromCloseElement.innerHTML = text;
        }
    }
}
